/**
 * CharacterSprite.ts - Animated character sprite system
 * Handles walk cycles, direction changes, and smooth movement interpolation
 */

import { SpriteManager } from './SpriteManager';
import { Renderer } from './Renderer';
import { ASSET_MANIFEST, getCharacterAnimation, FRAMES_PER_CHARACTER_ROW } from './data/AssetManifest';

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface CharacterSpriteConfig {
  characterClass: string;
  x: number;
  y: number;
  tileSize?: number;
}

export class CharacterSprite {
  private spriteManager: SpriteManager;
  private characterClass: string;

  // Position (in pixels)
  private x: number;
  private y: number;
  private targetX: number;
  private targetY: number;

  // Animation state
  private direction: Direction = 'down';
  private isMoving: boolean = false;
  private animFrame: number = 0;
  private animTimer: number = 0;
  private currentAnimation: number[] = [];

  // Configuration
  private readonly tileSize: number;
  private readonly moveSpeed: number = 2; // Pixels per frame
  private readonly animSpeed: number = 150; // MS per frame

  // Sprite sheet info
  private readonly sheetName: string = 'characters';
  private baseFrameIndex: number = 0;

  constructor(spriteManager: SpriteManager, config: CharacterSpriteConfig) {
    this.spriteManager = spriteManager;
    this.characterClass = config.characterClass;
    this.x = config.x;
    this.y = config.y;
    this.targetX = config.x;
    this.targetY = config.y;
    this.tileSize = config.tileSize ?? 16;

    // Calculate base frame for this character class
    const classRow = ASSET_MANIFEST.characterClassRows[this.characterClass] ?? 0;
    this.baseFrameIndex = classRow * FRAMES_PER_CHARACTER_ROW;

    // Set initial animation
    this.setAnimation('idle');
  }

  /**
   * Set the current animation
   */
  setAnimation(type: 'idle' | 'walkDown' | 'walkUp' | 'walkLeft' | 'walkRight'): void {
    const animKey = type === 'idle' ? 'idle' : type;
    this.currentAnimation = getCharacterAnimation(this.characterClass, animKey as any);
    this.animFrame = 0;
    this.animTimer = 0;
  }

  /**
   * Set direction (updates animation)
   */
  setDirection(direction: Direction): void {
    if (this.direction === direction) return;

    this.direction = direction;

    // Update animation based on movement state
    if (this.isMoving) {
      const animType = `walk${direction.charAt(0).toUpperCase()}${direction.slice(1)}` as any;
      this.setAnimation(animType);
    }
  }

  /**
   * Start moving to a target position
   */
  moveTo(targetX: number, targetY: number, direction: Direction): void {
    this.targetX = targetX;
    this.targetY = targetY;
    this.isMoving = true;
    this.setDirection(direction);

    // Start walk animation
    const animType = `walk${direction.charAt(0).toUpperCase()}${direction.slice(1)}` as any;
    this.setAnimation(animType);
  }

  /**
   * Instantly teleport to position
   */
  setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.targetX = x;
    this.targetY = y;
    this.isMoving = false;
  }

  /**
   * Update sprite animation and movement
   */
  update(deltaTime: number): void {
    // Update movement interpolation
    if (this.isMoving) {
      this.updateMovement(deltaTime);
    }

    // Update animation
    this.updateAnimation(deltaTime);
  }

  private updateMovement(_deltaTime: number): void {
    // Move towards target
    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist <= this.moveSpeed) {
      // Arrived at target
      this.x = this.targetX;
      this.y = this.targetY;
      this.isMoving = false;
      this.setAnimation('idle');
    } else {
      // Move towards target
      this.x += (dx / dist) * this.moveSpeed;
      this.y += (dy / dist) * this.moveSpeed;
    }
  }

  private updateAnimation(deltaTime: number): void {
    if (this.currentAnimation.length === 0) return;

    this.animTimer += deltaTime;

    if (this.animTimer >= this.animSpeed) {
      this.animTimer = 0;
      this.animFrame = (this.animFrame + 1) % this.currentAnimation.length;
    }
  }

  /**
   * Render the character sprite
   */
  render(renderer: Renderer, screenX: number, screenY: number): void {
    // Get current frame
    const frameIndex = this.currentAnimation[this.animFrame] ?? this.baseFrameIndex;

    // Try to get sprite from sprite manager
    const sprite = this.spriteManager.getSprite(this.sheetName, frameIndex);

    if (sprite) {
      // Flip sprite for left direction (right sprite flipped)
      const flipX = this.direction === 'left';
      renderer.drawSprite(sprite, screenX, screenY, flipX);
    } else {
      // Fallback: draw colored rectangle based on class
      const classColors: Record<string, string> = {
        'FIGHTER': '#ff4444',
        'THIEF': '#44ff44',
        'MONK': '#ffaa00',
        'RED_MAGE': '#ff4488',
        'WHITE_MAGE': '#ffffff',
        'BLACK_MAGE': '#4444ff',
        'KNIGHT': '#ff8888',
        'NINJA': '#88ff88',
        'MASTER': '#ffcc44',
        'RED_WIZARD': '#ff88aa',
        'WHITE_WIZARD': '#ffffcc',
        'BLACK_WIZARD': '#8888ff',
      };

      const color = classColors[this.characterClass] ?? '#ff00ff';
      renderer.drawRect(screenX, screenY, this.tileSize, this.tileSize, color, true);
      renderer.drawRect(screenX, screenY, this.tileSize, this.tileSize, '#ffffff', false);

      // Draw direction indicator
      const cx = screenX + this.tileSize / 2;
      const cy = screenY + this.tileSize / 2;
      let ix = cx, iy = cy;

      switch (this.direction) {
        case 'up': iy -= 4; break;
        case 'down': iy += 4; break;
        case 'left': ix -= 4; break;
        case 'right': ix += 4; break;
      }

      renderer.drawRect(ix - 2, iy - 2, 4, 4, '#ffff00', true);
    }
  }

  /**
   * Render with shadow (for world map)
   */
  renderWithShadow(renderer: Renderer, screenX: number, screenY: number): void {
    // Draw shadow
    renderer.drawRect(
      screenX + 2,
      screenY + this.tileSize - 4,
      this.tileSize - 4,
      4,
      'rgba(0, 0, 0, 0.3)',
      true
    );

    // Draw character
    this.render(renderer, screenX, screenY);
  }

  // Getters
  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  getDirection(): Direction {
    return this.direction;
  }

  isCurrentlyMoving(): boolean {
    return this.isMoving;
  }

  getCharacterClass(): string {
    return this.characterClass;
  }

  /**
   * Change character class (for class upgrades)
   */
  setCharacterClass(newClass: string): void {
    this.characterClass = newClass;
    const classRow = ASSET_MANIFEST.characterClassRows[newClass] ?? 0;
    this.baseFrameIndex = classRow * FRAMES_PER_CHARACTER_ROW;
    this.setAnimation(this.isMoving ? `walk${this.direction.charAt(0).toUpperCase()}${this.direction.slice(1)}` as any : 'idle');
  }
}
